import { useEffect, useRef, useState, useCallback } from "react";
import { Tooltip } from "flowbite-react";

const TruncateTooltip = ({ text, children, className = "" }: any) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    
    const isNowTruncated = el.scrollWidth > el.clientWidth + 2;
    setIsTruncated(isNowTruncated);
  }, []);

  useEffect(() => {
    checkTruncation();
  }, [text, checkTruncation]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(checkTruncation);
    const mutationObserver = new MutationObserver(checkTruncation);

    resizeObserver.observe(el);
    mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });

    const timeout = setTimeout(checkTruncation, 100);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      clearTimeout(timeout);
    };
  }, [checkTruncation]);

  const label = typeof text === "string" ? text : typeof children === "string" ? children : undefined;

  const content = (
    <span
      ref={ref}
      tabIndex={isTruncated ? 0 : undefined}
      aria-label={isTruncated ? label : undefined}
      className={`block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-orgPrimary rounded-sm ${className}`}
    >
      {children}
    </span>
  );

  return (
    <Tooltip
      content={text || children}
      trigger="hover"
      style="dark"
      placement="top"
      theme={{
        target: "w-full",
        base: "w-fit max-w-xs p-2.5 rounded-md shadow-md text-sm text-white font-medium break-words",
      }}
    >
      {content}
    </Tooltip>
  );
};

export default TruncateTooltip;